<template>
  <div :class="[$style.container, 'col-sm-5']" data-testid="price-value-is24">
    <HgCardFlat>
      <div v-if="isLoading" :class="$style.loadingWrapper" data-testid="price-value-skeleton">
        <HgSkeleton height="30px" width="220px" />
        <HgSkeleton height="100px" />
        <HgSkeleton height="20px" width="250px" />
      </div>
      <template v-else>
        <h3 :class="$style.heading">{{ $t('localLanding.priceValue.title') }}</h3>
        <p v-if="!priceData.price" :class="$style.emptyPlaceholder">
          {{ $t('localLanding.priceDevelopment.noData') }}
        </p>
        <template v-else>
          <div :class="$style.priceContentWrapper">
            <p :class="$style.price">
              {{ priceData.price }} CHF/<span>m<sup>2</sup></span>
            </p>
            <p :class="$style.range">
              {{
                $t('localLanding.priceValue.range', {
                  min: priceData.range.min,
                  max: priceData.range.max,
                })
              }}
              CHF/<span>m<sup>2</sup></span>
            </p>
            <p :class="$style.date">
              {{
                $t('localLanding.priceValue.date', {
                  date: priceData.date,
                })
              }}
            </p>
          </div>
          <HgLink
            name="priceCardRedirect"
            :class="$style.link"
            :href="valuationLink"
            @click.prevent="handleLinkClick()"
          >
            {{ $t(`localLanding.priceValue.link.${propertyType}`) }}
          </HgLink>
        </template>
      </template>
    </HgCardFlat>
  </div>
</template>

<script setup lang="ts">
import adaptPriceData, {
  getDefaultPriceValue,
  MedianPriceDataItem,
} from '@/components/local-landing-page/PriceDevelopment/adaptPriceData';
import { VALUATION_FUNNEL_PATH } from '@/router/routes/constants';
import { useLocalLandingStore } from '@/stores/localLanding/localLanding';
import { trackingMapInteractionEvent } from '@/tracking/tracking';
import { LOCAL_LANDING_PAGE_ACTION } from '@/tracking/types';
import { HgCardFlat, HgLink, HgSkeleton, HgSupportedLanguage } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

// Composables
const localLandingStore = useLocalLandingStore();
const { t, locale } = useI18n();
const router = useRouter();
const {
  propertyType,
  priceData: rawPriceData,
  isPriceDataLoading,
  isResolvingLocation,
} = storeToRefs(localLandingStore);

// Reactive data
const priceData = ref<MedianPriceDataItem>(getDefaultPriceValue());

// Other computed properties
const isLoading = computed(() => {
  return isResolvingLocation.value || isPriceDataLoading.value;
});

const valuationLink = computed(() => {
  return `${VALUATION_FUNNEL_PATH[locale.value]}`;
});

// Methods
const handlePriceData = () => {
  const data = adaptPriceData(rawPriceData.value.price, false, locale.value as HgSupportedLanguage);
  priceData.value = data[propertyType.value];
};

const handleLinkClick = () => {
  router.push(valuationLink.value); // via vue-router with the client side transition
  trackingMapInteractionEvent(
    LOCAL_LANDING_PAGE_ACTION.VALUATION_FUNNEL,
    'current_market_price_box',
  );
};

// Watchers
watch(locale, handlePriceData);
watch(propertyType, handlePriceData);
watch(rawPriceData, handlePriceData, { immediate: true });
</script>

<style module lang="scss">
@use '@/assets/variables' as *;

.container {
  height: inherit;
  color: $color-grey-100;

  &[class*='col-'] {
    padding: 0;
  }

  [class*='HgCardFlat_cardFlat'] {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: $space-150;
    justify-content: space-between;
    height: inherit;
    padding: $space-100;
    border: unset;
    box-shadow: $elevation-minimal;

    @include grid-media-query(sm) {
      align-items: center;
    }
  }
}

.loadingWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-150;
  width: 100%;
  height: 100%;

  @include grid-media-query(sm) {
    align-items: center;
    justify-content: space-between;
  }

  > span {
    border-radius: $border-radius-s;

    &:last-child {
      align-self: center;
    }
  }
}

.emptyPlaceholder {
  align-self: center;
  color: $color-grey-90;

  @include grid-media-query(sm) {
    margin-bottom: $space-600;
  }

  @include body-3;
}

.heading {
  @include heading-3;
  @include vertical-margin;
}

.priceContentWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-50;
  align-items: center;

  > p {
    @include vertical-margin;
  }
}

.price {
  @include vertical-margin;
  @include body-1;

  & {
    line-height: $space-200;
  }

  @include grid-media-query(sm) {
    @include body-2;

    & {
      font-size: 1.5rem;
      line-height: 22px;
    }
  }
}

.range {
  @include body-3;
}

.date {
  @include caption($color-grey-90);
}

.link {
  align-self: center;

  @include body-2-bold;
}
</style>
